import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  timer,
  map,
  switchMap,
  takeWhile,
  last,
  catchError,
  finalize
} from 'rxjs';
import { GraphService } from 'src/app/services/api/graph.service';
import {
  createGraphqlClientError,
  logOrderDevDetail
} from 'src/entities/order/lib/graphql-error.utils';
import {
  CREATE_PURCHASE_NOTE_JOB_POLL_INTERVAL_MS,
  CREATE_PURCHASE_NOTE_JOB_TIMEOUT_MS,
  CreatePurchaseNoteInput,
  CreatePurchaseNoteJobResult,
  CreatePurchaseNoteJobStatus,
  isCreatePurchaseNoteJobInProgress
} from '../model/create-purchase-note.model';
import {
  DeletePurchaseNoteInput,
  DeletePurchaseNoteResult,
  UpdatePurchaseNoteByIdInput,
  UpdatePurchaseNoteReservationNumberInput,
  UpdatePurchaseNoteReservationStatusInput,
  UpdatePurchaseNoteResult,
  UpdatePurchaseNoteReviewDateInput
} from '../model/update-purchase-note.model';
import {
  PurchaseNotePdfDownloadUrlResult,
  RetryGeneratePurchaseNotePdfResult
} from '../model/purchase-note-pdf.model';
import { ServicePurchaseNote } from '../model/purchase-note.model';
import {
  CREATE_PURCHASE_NOTE_MUTATION,
  CREATE_PURCHASE_NOTE_PDF_DOWNLOAD_URL_MUTATION,
  DELETE_PURCHASE_NOTE_MUTATION,
  GET_CREATE_PURCHASE_NOTE_JOB_QUERY,
  GET_PURCHASE_NOTES_BY_IDS_QUERY,
  RETRY_GENERATE_PURCHASE_NOTE_PDF_MUTATION,
  UPDATE_PURCHASE_NOTE_BY_ID_MUTATION,
  UPDATE_PURCHASE_NOTE_RESERVATION_NUMBER_MUTATION,
  UPDATE_PURCHASE_NOTE_RESERVATION_STATUS_MUTATION,
  UPDATE_PURCHASE_NOTE_REVIEW_DATE_MUTATION
} from './purchase-note.mutations';

@Injectable({ providedIn: 'root' })
export class PurchaseNoteService {
  private readonly createBusySubject = new BehaviorSubject<boolean>(false);
  readonly createPurchaseNoteBusy$ = this.createBusySubject.asObservable();

  constructor(private readonly graph: GraphService) {}

  get isCreatePurchaseNoteBusy(): boolean {
    return this.createBusySubject.value;
  }

  createPurchaseNote(
    input: CreatePurchaseNoteInput
  ): Observable<CreatePurchaseNoteJobResult> {
    return this.graph
      .mutateGraphQLCore<{ createPurchaseNote: CreatePurchaseNoteJobResult }>({
        mutation: CREATE_PURCHASE_NOTE_MUTATION,
        variables: { input }
      })
      .pipe(map((data: any) => this.normalizeJob(data?.createPurchaseNote)));
  }

  getCreatePurchaseNoteJob(
    jobId: string
  ): Observable<CreatePurchaseNoteJobResult> {
    return this.graph
      .queryGraphQLCore<{
        getCreatePurchaseNoteJob: CreatePurchaseNoteJobResult;
      } | {
        data?: { getCreatePurchaseNoteJob: CreatePurchaseNoteJobResult };
        errors?: Array<{ message: string; extensions?: { code?: string } }>;
      }>({
        query: GET_CREATE_PURCHASE_NOTE_JOB_QUERY,
        variables: { jobId }
      })
      .pipe(
        map((response: any) => {
          if (response?.errors?.length) {
            throw createGraphqlClientError(response.errors);
          }

          const job =
            response?.getCreatePurchaseNoteJob ??
            response?.data?.getCreatePurchaseNoteJob;

          return this.normalizeJob(job);
        })
      );
  }

  createPurchaseNoteWithPolling(
    input: CreatePurchaseNoteInput
  ): Observable<CreatePurchaseNoteJobResult> {
    const startedAt = Date.now();
    this.createBusySubject.next(true);

    return this.createPurchaseNote(input).pipe(
      switchMap((job) => this.waitForJob(job, startedAt)),
      finalize(() => this.createBusySubject.next(false))
    );
  }

  getPurchaseNotesByIds(
    purchaseNoteIds: string[]
  ): Observable<ServicePurchaseNote[]> {
    const ids = [...new Set(purchaseNoteIds.map((id) => String(id).trim()).filter(Boolean))];
    if (!ids.length) {
      return of([]);
    }

    return this.graph
      .queryGraphQLCore<{
        getPurchaseNotesByIds: ServicePurchaseNote[];
      }>({
        query: GET_PURCHASE_NOTES_BY_IDS_QUERY,
        variables: { purchaseNoteIds: ids }
      })
      .pipe(map((data: any) => data?.getPurchaseNotesByIds ?? []));
  }

  createPurchaseNotePdfDownloadUrl(
    purchaseNoteId: string,
    disposition?: 'inline' | 'attachment' | null
  ): Observable<PurchaseNotePdfDownloadUrlResult> {
    const variables: { purchaseNoteId: string; disposition?: string } = {
      purchaseNoteId
    };
    if (disposition) {
      variables.disposition = disposition;
    }

    return this.graph
      .mutateGraphQLCore<{
        createPurchaseNotePdfDownloadUrl: PurchaseNotePdfDownloadUrlResult;
      }>({
        mutation: CREATE_PURCHASE_NOTE_PDF_DOWNLOAD_URL_MUTATION,
        variables
      })
      .pipe(map((data: any) => data?.createPurchaseNotePdfDownloadUrl));
  }

  retryGeneratePurchaseNotePdf(
    purchaseNoteId: string
  ): Observable<RetryGeneratePurchaseNotePdfResult> {
    return this.graph
      .mutateGraphQLCore<{
        retryGeneratePurchaseNotePdf: RetryGeneratePurchaseNotePdfResult;
      }>({
        mutation: RETRY_GENERATE_PURCHASE_NOTE_PDF_MUTATION,
        variables: { purchaseNoteId }
      })
      .pipe(map((data: any) => data?.retryGeneratePurchaseNotePdf));
  }

  deletePurchaseNote(
    input: DeletePurchaseNoteInput
  ): Observable<DeletePurchaseNoteResult> {
    return this.mutatePurchaseNoteUpdate(
      DELETE_PURCHASE_NOTE_MUTATION,
      input,
      'deletePurchaseNote'
    );
  }

  updatePurchaseNoteReviewDate(
    input: UpdatePurchaseNoteReviewDateInput
  ): Observable<UpdatePurchaseNoteResult> {
    return this.mutatePurchaseNoteUpdate(
      UPDATE_PURCHASE_NOTE_REVIEW_DATE_MUTATION,
      input,
      'updatePurchaseNoteReviewDate'
    );
  }

  updatePurchaseNoteReservationNumber(
    input: UpdatePurchaseNoteReservationNumberInput
  ): Observable<UpdatePurchaseNoteResult> {
    return this.mutatePurchaseNoteUpdate(
      UPDATE_PURCHASE_NOTE_RESERVATION_NUMBER_MUTATION,
      input,
      'updatePurchaseNoteReservationNumber'
    );
  }

  updatePurchaseNoteReservationStatus(
    input: UpdatePurchaseNoteReservationStatusInput
  ): Observable<UpdatePurchaseNoteResult> {
    return this.mutatePurchaseNoteUpdate(
      UPDATE_PURCHASE_NOTE_RESERVATION_STATUS_MUTATION,
      input,
      'updatePurchaseNoteReservationStatus'
    );
  }

  updatePurchaseNoteById(
    input: UpdatePurchaseNoteByIdInput
  ): Observable<UpdatePurchaseNoteResult> {
    return this.mutatePurchaseNoteUpdate(
      UPDATE_PURCHASE_NOTE_BY_ID_MUTATION,
      input,
      'updatePurchaseNoteById'
    );
  }

  private mutatePurchaseNoteUpdate(
    mutation: string,
    input: unknown,
    resultKey: string
  ): Observable<UpdatePurchaseNoteResult> {
    return this.graph
      .mutateGraphQLCore<Record<string, UpdatePurchaseNoteResult>>({
        mutation,
        variables: { input }
      })
      .pipe(map((data: any) => data?.[resultKey]));
  }

  private waitForJob(
    initialJob: CreatePurchaseNoteJobResult,
    startedAt: number
  ): Observable<CreatePurchaseNoteJobResult> {
    const terminalOrThrow = (
      job: CreatePurchaseNoteJobResult
    ): Observable<CreatePurchaseNoteJobResult> => {
      const status = job.status?.trim().toUpperCase();

      if (status === CreatePurchaseNoteJobStatus.COMPLETED) {
        // Si el create solo trajo jobId/status (sin arrays), hidrata con getCreatePurchaseNoteJob.
        const hasPayload =
          (job.createdPurchaseNotes?.length ?? 0) > 0 ||
          (job.rejectedServices?.length ?? 0) > 0 ||
          (job.warnings?.length ?? 0) > 0;

        if (!hasPayload && job.jobId) {
          return this.getCreatePurchaseNoteJob(job.jobId);
        }

        return of(job);
      }

      if (status === CreatePurchaseNoteJobStatus.FAILED) {
        logOrderDevDetail('createPurchaseNote.lastError', job.lastError);
        return throwError(() => ({
          graphQLErrors: [
            {
              message: job.lastError || 'CREATE_PURCHASE_NOTE_JOB_FAILED',
              extensions: {
                code: job.errorCode || 'CREATE_PURCHASE_NOTE_JOB_FAILED'
              }
            }
          ]
        }));
      }

      if (Date.now() - startedAt > CREATE_PURCHASE_NOTE_JOB_TIMEOUT_MS) {
        return throwError(() => ({
          graphQLErrors: [
            {
              message: 'TIMEOUT_CREATE_PURCHASE_NOTE_JOB',
              extensions: { code: 'TIMEOUT_CREATE_PURCHASE_NOTE_JOB' }
            }
          ]
        }));
      }

      return of(job);
    };

    if (!isCreatePurchaseNoteJobInProgress(initialJob.status)) {
      return terminalOrThrow(initialJob);
    }

    return timer(0, CREATE_PURCHASE_NOTE_JOB_POLL_INTERVAL_MS).pipe(
      switchMap(() => {
        if (Date.now() - startedAt > CREATE_PURCHASE_NOTE_JOB_TIMEOUT_MS) {
          return throwError(() => ({
            graphQLErrors: [
              {
                message: 'TIMEOUT_CREATE_PURCHASE_NOTE_JOB',
                extensions: { code: 'TIMEOUT_CREATE_PURCHASE_NOTE_JOB' }
              }
            ]
          }));
        }

        return this.getCreatePurchaseNoteJob(initialJob.jobId);
      }),
      takeWhile((job) => isCreatePurchaseNoteJobInProgress(job.status), true),
      last(),
      switchMap((job) => terminalOrThrow(job)),
      catchError((error) => throwError(() => error))
    );
  }

  private normalizeJob(job: any): CreatePurchaseNoteJobResult {
    return {
      jobId: job?.jobId ?? '',
      status: job?.status ?? CreatePurchaseNoteJobStatus.FAILED,
      errorCode: job?.errorCode ?? null,
      lastError: job?.lastError ?? null,
      createdPurchaseNotes: job?.createdPurchaseNotes ?? [],
      rejectedServices: job?.rejectedServices ?? [],
      warnings: job?.warnings ?? [],
      createdAt: job?.createdAt ?? null,
      updatedAt: job?.updatedAt ?? null
    };
  }
}
